// frontend-repo/src/hooks/useBackendContext.js
import { useCallback, useEffect, useState } from "react";
import {
  getFromCache,
  setInCache,
  CACHE_TTL,
  CACHE_KEYS,
} from "../utils/cacheUtils";

export const useBackendContext = () => {
  const [backendContext, setBackendContext] = useState(null);
  const [fullContext, setFullContext] = useState(null);
  const [processContext, setProcessContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const host = process.env.REACT_APP_SERVER;

  // Load lightweight summary context first (with localStorage caching)
  useEffect(() => {
    const loadInitialContext = async () => {
      try {
        setLoading(true);

        // Check localStorage cache first
        const cachedContext = getFromCache(CACHE_KEYS.context(), CACHE_TTL.CONTEXT);
        if (cachedContext) {
          console.log("⚡ Using cached backend context");
          setFullContext(cachedContext);
          const summaryContext = createSummaryContext(cachedContext);
          setBackendContext(summaryContext);
          setLoading(false);
          return;
        }

        // Debug: Log the URL being used
        const contextUrl =
          process.env.REACT_APP_CONTEXT_CDN_URL ||
          "https://s3.amazonaws.com/monika-codebase-context-1750423261/contexts/backend-context.json";

        console.log("🔗 Attempting to load context from:", contextUrl);

        // Try different URL formats
        const urlsToTry = [
          contextUrl,
          "https://monika-codebase-context-1750423261.s3.amazonaws.com/contexts/backend-context.json",
          "https://monika-codebase-context-1750423261.s3.us-east-1.amazonaws.com/contexts/backend-context.json",
        ];

        let fullData = null;
        let lastError = null;

        for (const url of urlsToTry) {
          try {
            console.log(`🔄 Trying URL: ${url}`);

            const response = await fetch(url, {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
              mode: "cors",
            });

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            fullData = await response.json();
            console.log(`✅ Success with URL: ${url}`);
            break;
          } catch (urlError) {
            console.warn(`❌ Failed with ${url}:`, urlError.message);
            lastError = urlError;
            continue;
          }
        }

        if (!fullData) {
          throw lastError || new Error("All URLs failed");
        }

        // Cache the full context for future loads
        setInCache(CACHE_KEYS.context(), fullData);

        setFullContext(fullData);

        // Create lightweight summary for initial load
        const summaryContext = createSummaryContext(fullData);
        setBackendContext(summaryContext);

        console.log("✅ Backend context loaded and cached:", {
          fullSize: `${Math.round(JSON.stringify(fullData).length / 1024)}KB`,
          summarySize: `${Math.round(JSON.stringify(summaryContext).length / 1024)}KB`,
          totalFiles: fullData.metadata.totalFiles,
          pythonFiles: fullData.metadata.pythonFiles,
        });
      } catch (err) {
        console.error("❌ Context loading failed:", err);
        setError(err.message);

        // Fallback to mock context for development
        if (process.env.NODE_ENV === "development") {
          setBackendContext(createMockContext());
          console.warn("🔄 Using mock context for development");
        }
      } finally {
        setLoading(false);
      }
    };

    loadInitialContext();
  }, []);

  // Load live process data from introspection endpoint (with caching)
  useEffect(() => {
    const loadProcessContext = async () => {
      if (!host) {
        console.warn("⚠️ No REACT_APP_SERVER host configured for process context");
        return;
      }

      // Check cache first
      const cachedProcessContext = getFromCache(
        CACHE_KEYS.processContext(),
        CACHE_TTL.PROCESS_LIST
      );
      if (cachedProcessContext) {
        console.log("⚡ Using cached process context");
        setProcessContext(cachedProcessContext);
        return;
      }

      console.log("🔄 Fetching process context from:", `${host}/api/processes/introspect`);

      try {
        const response = await fetch(`${host}/api/processes/introspect`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        if (data.status === "success" && data.data) {
          const processes = data.data.processes || [];

          // Group processes by type for easier context building
          const models = processes.filter(p => p.type === "model");
          const mutations = processes.filter(p => p.type === "mutation");
          const metrics = processes.filter(p => p.type === "metric");

          const processData = {
            totalProcesses: processes.length,
            // Keep ALL data from each model for rich context
            models: models.map(m => ({
              name: m.name,
              function: m.function,
              description: m.description,
              description_long: m.description_long,
              example: m.example,
              resources: m.resources,
              group: m.group,
              source: m.source_file,
              proportion: m.proportion,
              category: m.config_category,
              enabled: m.enabled,
              applicable_mutations: m.applicable_mutations,
              parameters: m.parameters,
              inputs: m.inputs,
            })),
            // Keep ALL data from each mutation
            mutations: mutations.map(m => ({
              name: m.name,
              function: m.function,
              description: m.description,
              description_long: m.description_long,
              example: m.example,
              resources: m.resources,
              source: m.source_file,
              mutation_type: m.mutation_type, // HEAVY/LIGHT/MANUAL
              parameters: m.parameters,
              inputs: m.inputs,
            })),
            // Include metrics
            metrics: metrics.map(m => ({
              name: m.name,
              function: m.function,
              description: m.description,
              source: m.source_file,
            })),
            // Include summary data
            byType: data.data.by_type || {},
            globalConfig: data.data.global_config || {},
            modelCategories: data.data.model_categories || {},
            mutationTypes: data.data.mutation_types || {},
            trademarkMutationRules: data.data.trademark_mutation_rules || {},
            modelMutationMappings: data.data.model_mutation_mappings || {},
            generatedAt: data.data.generated_at,
            // Git info from live introspection
            gitCommit: data.data.git_commit,
            gitBranch: data.data.git_branch,
          };

          // Cache the process context
          setInCache(CACHE_KEYS.processContext(), processData);

          setProcessContext(processData);
          console.log("✅ Process context loaded and cached:", {
            models: models.length,
            mutations: mutations.length,
            metrics: metrics.length,
            hasGlobalConfig: !!data.data.global_config,
            categories: Object.keys(data.data.model_categories || {}).length,
          });
        }
      } catch (err) {
        console.warn("⚠️ Failed to load process context:", err.message);
        // Non-critical - don't set error state
      }
    };

    loadProcessContext();
  }, [host]);

  // Smart context selection based on user query
  const getContextForQuery = useCallback(
    query => {
      if (!fullContext) return backendContext;

      const queryLower = query.toLowerCase();
      let selectedContext = { ...backendContext };

      // Add specific files based on query keywords
      const relevantFiles = {};
      const maxContextSize = 100000; // ~100KB limit
      let currentSize = JSON.stringify(backendContext).length;

      // Priority keywords to file mappings
      const keywordMappings = {
        handler: ["python/handlers", "handlers"],
        api: ["api/", "routes", "endpoints"],
        database: ["python/database", "db", "models"],
        metric: ["python/metrics", "metrics"],
        auth: ["auth", "login", "session"],
        config: ["config", "settings", "env"],
        test: ["test", "spec", "__test__"],
      };

      // Find relevant files based on query
      for (const [keyword, patterns] of Object.entries(keywordMappings)) {
        if (queryLower.includes(keyword)) {
          for (const [filePath, fileInfo] of Object.entries(fullContext.files || {})) {
            if (patterns.some(pattern => filePath.toLowerCase().includes(pattern))) {
              const fileSize = JSON.stringify(fileInfo).length;
              if (currentSize + fileSize < maxContextSize) {
                relevantFiles[filePath] = fileInfo;
                currentSize += fileSize;
              }
            }
          }
        }
      }

      // Add high-importance files if space allows
      if (currentSize < maxContextSize * 0.7) {
        for (const [filePath, fileInfo] of Object.entries(fullContext.files || {})) {
          if (fileInfo.importance === "high") {
            const fileSize = JSON.stringify(fileInfo).length;
            if (currentSize + fileSize < maxContextSize) {
              relevantFiles[filePath] = fileInfo;
              currentSize += fileSize;
            }
          }
        }
      }

      if (Object.keys(relevantFiles).length > 0) {
        selectedContext.files = relevantFiles;
        selectedContext.querySpecific = true;

        console.log("🎯 Context selected for query:", {
          query: query.substring(0, 50) + "...",
          filesSelected: Object.keys(relevantFiles).length,
          contextSize: `${Math.round(currentSize / 1024)}KB`,
        });
      }

      return selectedContext;
    },
    [fullContext, backendContext]
  );

  return {
    backendContext,
    fullContext,
    processContext,
    loading,
    error,
    getContextForQuery,
  };
};

// Create lightweight summary context (~10-20KB)
function createSummaryContext(fullContext) {
  return {
    metadata: fullContext.metadata,
    structure: fullContext.structure,

    // Top API endpoints only
    apiEndpoints: (fullContext.apiEndpoints || []).slice(0, 15),

    // High-importance Python modules only
    pythonModules: (fullContext.pythonModules || [])
      .filter(mod => mod.importance === "high")
      .slice(0, 10)
      .map(mod => ({
        file: mod.file,
        functions: mod.functions,
        classes: mod.classes,
        importance: mod.importance,
      })),

    // Critical files metadata only (no content)
    files: Object.fromEntries(
      Object.entries(fullContext.files || {})
        .filter(([_, info]) => info.importance === "high")
        .slice(0, 20)
        .map(([path, info]) => [
          path,
          {
            fileType: info.fileType,
            importance: info.importance,
            lines: info.lines || info.totalLines,
            lastModified: info.lastModified,
            // Remove content to save space
            functions: info.functions?.slice(0, 3) || [],
            classes: info.classes?.slice(0, 3) || [],
          },
        ])
    ),

    // Environment info
    pythonEnvironment: fullContext.pythonEnvironment,

    // Mark as summary
    isSummary: true,
    loadedAt: new Date().toISOString(),
  };
}

// Fallback mock context for development
function createMockContext() {
  return {
    metadata: {
      repository: "backend",
      generatedAt: new Date().toISOString(),
      gitCommit: "mock-commit-hash",
      gitBranch: "main",
      totalFiles: 0,
      pythonFiles: 0,
      javascriptFiles: 0,
    },
    structure: {},
    apiEndpoints: [],
    pythonModules: [],
    files: {},
    isMock: true,
    loadedAt: new Date().toISOString(),
  };
}
